import { BriefcaseIcon, CalendarIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { formatMoney } from '@/lib/utils';

import { formatDateTime, invoicePhase, invoicePhaseColorClass, invoicePhaseLabel } from './helpers';
import type { Invoice } from './types';

interface Props {
  invoice: Invoice;
  onSelect: (invoice: Invoice) => void;
}

export function InvoiceCard({ invoice, onSelect }: Props) {
  const { t } = useLanguage();
  const phase = invoicePhase(invoice);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(invoice)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(invoice);
      }}
      className="bg-card border rounded-xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow cursor-pointer"
      data-testid={`invoice-card-${invoice.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm flex items-center gap-1.5 truncate">
            <BriefcaseIcon className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{invoice.job_title ?? '—'}</span>
          </p>
        </div>
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${invoicePhaseColorClass(phase)}`}
        >
          {invoicePhaseLabel(phase, t)}
        </span>
      </div>

      <p className="text-lg font-semibold">{formatMoney(invoice.total_amount)}</p>

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
        <span>
          {t.invoiceCreatedAt} {formatDateTime(invoice.created_at)}
        </span>
      </div>
    </div>
  );
}
